import React from 'react';
import LockContext from 'contexts/lock_ressource/context';
import { useSession } from 'hooks/use_context';
import { useEffect, useRef, useState } from 'react';
import { checkLockOwner, deleteLock, lockRessources } from 'requests/lock';
import { useI18n } from 'hooks/use_i18n';
import { SHARE } from 'constants';
import { useLocation } from 'react-router-dom';

export const LockProvider = ({ children }) => {
  const { currentUser } = useSession();
  const { t } = useI18n();
  const [lockedBy, setLockedBy] = useState(null);
  const [blockId, setBlockId] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [isRessourceAvailable, setIsRessourceAvailable] = useState(false);
  const [tabId] = useState(() => Math.random().toString(36).substring(7));
  const { pathname } = useLocation();
  const previousPathnameRef = useRef(pathname);

  const canEdit = !blockId || (isRessourceAvailable && lockedBy === currentUser?.display_name);

  useEffect(() => {
    // Ignore first render; only react to actual route changes.
    if (previousPathnameRef.current === pathname) return;

    previousPathnameRef.current = pathname;

    if (blockId && canEdit) {
      unlock(blockId);
    } else if (blockId) {
      setLockedBy(null);
      setIsRessourceAvailable(false);
      setBlockId(null);
      setWorkflow(null);
    }
  }, [pathname]);

  useEffect(() => {
    const handleBeforeUnload = async () => {
      if (blockId && canEdit) {
        try {
          await deleteLock(blockId);
        } catch (error) {
          console.error('Erreur lors de la suppression du lock au refresh:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [blockId, lockedBy, isRessourceAvailable]);

  useEffect(() => {
    if (blockId) {
      const checkLockStatus = async () => {
        try {
          const response = await checkLockOwner(blockId);

          if (response.status === 200) {
            return;
          }
        } catch (error) {
          if (error.status === 423) {
            const newOwner = error?.data?.lock?.user?.display_name;
            setIsRessourceAvailable(false);
            setLockedBy(newOwner);
          }
        }
      };

      checkLockStatus();

      const intervalId = setInterval(checkLockStatus, 10000);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [blockId, currentUser?.id]);

  const lock = async (blockId, workflow) => {
    if (SHARE) return;
    if (!blockId || !workflow) return;

    try {
      const checkResponse = await checkLockOwner(blockId);

      if (checkResponse.status === 423 || (checkResponse?.data?.lock && checkResponse.data.lock.user.id !== currentUser?.id)) {
        const lockOwner = checkResponse?.data?.lock?.user?.display_name;
        setBlockId(blockId);
        setWorkflow(workflow);
        setIsRessourceAvailable(false);
        setLockedBy(lockOwner);
        return;
      }

      const response = await lockRessources(blockId, workflow.id);
      if (response.status === 200) {
        setBlockId(blockId);
        setWorkflow(workflow);
        setIsRessourceAvailable(true);
        setLockedBy(currentUser?.display_name);
      }
    } catch (error) {
      if (error.status === 423) {
        setBlockId(blockId);
        setWorkflow(workflow);
        setIsRessourceAvailable(false);
        setLockedBy(error?.data?.lock?.user?.display_name);
      }
    }
  };

  const unlock = async (blockId) => {
    if (SHARE) return;
    if (!blockId) return;

    try {
      const checkResponse = await checkLockOwner(blockId);

      if (isRessourceAvailable && checkResponse.status === 200 && checkResponse.data.lock.user.id === currentUser?.id) {
        await deleteLock(blockId);
      }

      setLockedBy(null);
      setIsRessourceAvailable(false);
      setBlockId(null);
      setWorkflow(null);
    } catch (error) {
      console.error('Erreur lors de la vérification ou suppression du lock:', error);
      setLockedBy(null);
      setIsRessourceAvailable(false);
      setBlockId(null);
      setWorkflow(null);
    }
  };

  const debugLock = async (blockId, workflow) => {
    if (SHARE) return;
    if (!blockId || !workflow?.id) return;

    try {
      await deleteLock(blockId);

      const response = await lockRessources(blockId, workflow.id);

      if (response.status === 200) {
        setBlockId(blockId);
        setWorkflow(workflow);
        setIsRessourceAvailable(true);
        setLockedBy(currentUser?.display_name);
      }
    } catch (error) {
      console.error(error);
      setIsRessourceAvailable(false);
      setLockedBy(error?.data?.lock?.user?.display_name);
    }
  };

  const value = {
    lockedBy,
    blockId,
    workflow,
    isRessourceAvailable,
    canEdit,
    lock,
    unlock,
    setIsRessourceAvailable,
    debugLock,
  };

  return <LockContext.Provider value={value}>{children}</LockContext.Provider>;
};

// import React, { useEffect } from 'react';
// import { useLocation } from 'react-router-dom';
// import { useExternalServices } from 'hooks/use_external_services';

// const OnRouteChange = () => {
//   const { pathname } = useLocation();
//   const { trackPage, updateIntercom } = useExternalServices();

//   useEffect(() => {
//     console.log('OnRouteChange');
//     window.scrollTo(0, 0);
//     trackPage();
//     updateIntercom();
//   }, [pathname]);

//   return null;
// };

// export default OnRouteChange;
